import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { AlertTriangle, Trash2, Pencil, Check, X } from 'lucide-react';

// Currency codes we let users pick inline. Keep in sync with the generic
// extractor's _CURRENCY_CODES tuple. "—" clears the currency.
const CURRENCY_OPTIONS = [
  '', 'NGN', 'USD', 'EUR', 'GBP', 'ZAR', 'KES', 'GHS', 'AED',
  'JPY', 'CNY', 'INR', 'SGD', 'CAD', 'AUD', 'CHF', 'HKD',
];

// Suppress the browser's number spinner arrows on the amount input — the
// pattern below covers WebKit + Firefox + modern Chrome.
const NUMBER_NO_SPIN =
  '[appearance:textfield] [-moz-appearance:textfield] ' +
  '[&::-webkit-outer-spin-button]:appearance-none ' +
  '[&::-webkit-inner-spin-button]:appearance-none ' +
  '[&::-webkit-inner-spin-button]:m-0';

const formatValue = (v) =>
  v == null || v === '' ? '—' : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });

const ViewCells = ({ row, needsReview, index, disabled, onEdit, onDelete }) => (
  <>
    <td className="px-3 py-2 text-slate-800 align-top">
      <div className="flex items-start gap-2">
        {needsReview && (
          <AlertTriangle
            className="w-3.5 h-3.5 mt-1 text-amber-600 shrink-0"
            title="Please double-check this row"
          />
        )}
        <span className="text-sm" data-testid={`institution-import-preview-name-view-${index}`}>
          {row.asset_name || <span className="italic text-slate-400">Untitled</span>}
        </span>
      </div>
    </td>
    <td className="px-3 py-2 text-slate-600 align-top w-24 text-sm">
      {row.currency || <span className="text-slate-400">—</span>}
    </td>
    <td className="px-3 py-2 text-right text-slate-800 align-top w-36 text-sm tabular-nums">
      {formatValue(row.market_value)}
    </td>
    <td className="px-3 py-2 align-top w-20">
      <div className="flex items-center justify-end gap-1">
        <button
          type="button" onClick={onEdit} disabled={disabled}
          title="Edit this row"
          className="text-slate-400 hover:text-emerald-600 disabled:opacity-40 p-1"
          data-testid={`institution-import-preview-edit-${index}`}
        >
          <Pencil className="w-4 h-4" />
        </button>
        <button
          type="button" onClick={onDelete} disabled={disabled}
          title="Remove this row"
          className="text-slate-400 hover:text-red-600 disabled:opacity-40 p-1"
          data-testid={`institution-import-preview-delete-${index}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </td>
  </>
);

const EditCells = ({ draft, setDraft, disabled, index, onSave, onCancel }) => (
  <>
    <td className="px-3 py-2 align-top">
      <Input
        value={draft.asset_name || ''}
        onChange={(e) => setDraft({ ...draft, asset_name: e.target.value })}
        disabled={disabled}
        placeholder="Asset name"
        className="h-8 text-sm"
        autoFocus
        data-testid={`institution-import-preview-name-${index}`}
      />
    </td>
    <td className="px-3 py-2 align-top w-24">
      <select
        value={draft.currency || ''}
        onChange={(e) => setDraft({ ...draft, currency: e.target.value || null })}
        disabled={disabled}
        className="h-8 text-sm rounded-md border border-slate-300 bg-white px-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        data-testid={`institution-import-preview-currency-${index}`}
      >
        {CURRENCY_OPTIONS.map((c) => (
          <option key={c || 'none'} value={c}>{c || '—'}</option>
        ))}
      </select>
    </td>
    <td className="px-3 py-2 text-right align-top w-36">
      <Input
        type="number" step="any" inputMode="decimal"
        value={draft.market_value ?? ''}
        onChange={(e) => {
          const v = e.target.value;
          setDraft({ ...draft, market_value: v === '' ? null : Number(v) });
        }}
        disabled={disabled}
        placeholder="0"
        className={`h-8 text-sm text-right ${NUMBER_NO_SPIN}`}
        data-testid={`institution-import-preview-value-${index}`}
      />
    </td>
    <td className="px-3 py-2 align-top w-20">
      <div className="flex items-center justify-end gap-1">
        <button
          type="button" onClick={onSave} disabled={disabled}
          title="Save this row"
          className="text-slate-400 hover:text-emerald-600 disabled:opacity-40 p-1"
          data-testid={`institution-import-preview-save-${index}`}
        >
          <Check className="w-4 h-4" />
        </button>
        <button
          type="button" onClick={onCancel} disabled={disabled}
          title="Cancel edits"
          className="text-slate-400 hover:text-slate-700 disabled:opacity-40 p-1"
          data-testid={`institution-import-preview-cancel-${index}`}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </td>
  </>
);

// One row inside the preview table. Uses a view/edit toggle so the table
// reads like a normal read-only summary until the user actively chooses to
// edit — matching the standard pattern people expect.
const InstitutionImportPreviewRow = ({
  row, index, disabled, autoEdit = false, onChange, onDelete,
}) => {
  const [editing, setEditing] = useState(autoEdit);
  const [draft, setDraft] = useState(row);
  const needsReview = (row.confidence ?? 1) < 0.85;

  const startEdit = () => { setDraft(row); setEditing(true); };
  const cancelEdit = () => { setDraft(row); setEditing(false); };
  const saveEdit = () => { onChange({ ...draft, edited: true }); setEditing(false); };

  return (
    <tr
      className={
        editing ? 'bg-emerald-50/40'
          : needsReview ? 'bg-amber-50/60' : 'odd:bg-white even:bg-slate-50/50'
      }
      data-testid={`institution-import-preview-row-${index}`}
    >
      {editing ? (
        <EditCells
          draft={draft} setDraft={setDraft} disabled={disabled} index={index}
          onSave={saveEdit} onCancel={cancelEdit}
        />
      ) : (
        <ViewCells
          row={row} needsReview={needsReview} index={index}
          disabled={disabled} onEdit={startEdit} onDelete={onDelete}
        />
      )}
    </tr>
  );
};

export default InstitutionImportPreviewRow;
